import { Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { surfaceColor, surfaceText } from "../../../constants/surface-tokens"
import {
  type NotificationCounts,
  type NotificationFilter,
  NotificationFilterChips
} from "./NotificationFilterChips.tsx"

const { Text } = Typography

interface NotificationsPopoverHeaderProps {
  unreadCount: number
  counts?: NotificationCounts | null
  onMarkAllAsRead: () => void
  disabled?: boolean
  filter: NotificationFilter
  onFilterChange: (next: NotificationFilter) => void
  isCustomerActivityOnly: boolean
  onToggleCustomerActivityOnly: () => void
}

export function NotificationsPopoverHeader({
  unreadCount,
  counts,
  onMarkAllAsRead,
  disabled = false,
  filter,
  onFilterChange,
  isCustomerActivityOnly,
  onToggleCustomerActivityOnly
}: NotificationsPopoverHeaderProps) {
  const { t } = useTranslation()

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12
        }}
      >
        <h2
          style={{
            ...surfaceText.sectionTitle,
            fontSize: 18,
            margin: 0,
            color: surfaceColor.ink
          }}
        >
          {t("notifications.bell.heading", "Notifications")}
          {unreadCount > 0 && (
            <span
              data-testid="bell-unread-count"
              style={{
                ...surfaceText.chip,
                marginLeft: 8,
                padding: "2px 8px",
                borderRadius: 999,
                background: surfaceColor.tealPale,
                color: surfaceColor.teal,
                verticalAlign: "middle"
              }}
            >
              {unreadCount}
            </span>
          )}
        </h2>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {disabled && (
            <Text type="secondary" style={surfaceText.label}>
              {t("notifications.bell.offline")}
            </Text>
          )}
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={onMarkAllAsRead}
              disabled={disabled}
              data-testid="bell-mark-all-read"
              style={{
                border: "none",
                background: "none",
                padding: 0,
                cursor: disabled ? "not-allowed" : "pointer",
                ...surfaceText.button,
                color: disabled ? surfaceColor.inkMuted : surfaceColor.teal
              }}
            >
              {t("notifications.bell.markAllRead")}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <NotificationFilterChips
          value={filter}
          counts={counts}
          onChange={onFilterChange}
          size="small"
        />
        <CustomerActivityToggle
          pressed={isCustomerActivityOnly}
          onToggle={onToggleCustomerActivityOnly}
        />
      </div>
    </div>
  )
}

/**
 * A second axis over the three filters: whose activity this is, rather than
 * what kind of event it was. Kept apart from the chips because it composes
 * with each of them.
 */
export function CustomerActivityToggle({
  pressed,
  onToggle
}: {
  pressed: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <Tooltip title={t("notifications.bell.customerActivityFilterHint")}>
      <button
        type="button"
        aria-pressed={pressed}
        onClick={onToggle}
        data-testid="notification-filter-customer-activity"
        style={{
          ...surfaceText.button,
          fontSize: 12,
          padding: "4px 10px",
          borderRadius: 999,
          cursor: "pointer",
          border: `1px solid ${pressed ? surfaceColor.teal : surfaceColor.border}`,
          background: pressed ? surfaceColor.tealPale : surfaceColor.surface,
          color: pressed ? surfaceColor.teal : surfaceColor.inkMuted,
          whiteSpace: "nowrap"
        }}
      >
        {t("notifications.bell.customerActivityFilter")}
      </button>
    </Tooltip>
  )
}
